import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import { useLanguage } from '@/contexts/LanguageContext';

type SobrietyOption = 'relapse' | 'already-sober' | 'starting-today' | null;

export default function SobrietyClockScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [selectedOption, setSelectedOption] = useState<SobrietyOption>(null);

  useEffect(() => {
    // PRD §5.5 — `sobriety_clock_viewed`. This setup picker is the
    // canonical sobriety entry point in the current UX (the running clock
    // surfaces inside `/diary` only after setup). When a dedicated
    // dashboard screen ships, move the call there and document in PRD.
    import('@/utils/clarityEvents')
      .then(({ logSobrietyClockViewed }) => logSobrietyClockViewed())
      .catch(() => {});
  }, []);

  const handleOptionSelect = (option: SobrietyOption) => {
    setSelectedOption(option);
  };

  const handleContinue = () => {
    if (selectedOption) {
      switch (selectedOption) {
        case 'relapse':
          router.push('/sobriety-relapse');
          break;
        case 'already-sober':
          router.push('/sobriety-input');
          break;
        case 'starting-today':
          router.push('/sobriety-congratulations');
          break;
      }
    }
  };

  const handleCancel = () => {
    router.back();
  };

  const CancelButton = () => (
    <TouchableOpacity onPress={handleCancel}>
      <Text style={styles.cancelText}>{t('sobriety.clock.cancel')}</Text>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader 
        title={t('sobriety.clock.title')} 
        onBack={handleCancel}
        showBackButton={false}
        rightComponent={<CancelButton />}
      />

      <View style={styles.section}>
        <Text style={styles.questionText}>
          {t('sobriety.clock.question')}
        </Text>

        <View style={styles.optionsGroup}>
          <TouchableOpacity
            style={[
              styles.card,
              selectedOption === 'relapse' && styles.selectedCard
            ]}
            onPress={() => handleOptionSelect('relapse')}
          >
            <Text style={styles.optionText}>
              {t('sobriety.clock.option1')}
            </Text>
            <View style={[
              styles.radioButton,
              selectedOption === 'relapse' && styles.selectedRadio
            ]}>
              {selectedOption === 'relapse' && <View style={styles.radioInner} />}
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.card,
              selectedOption === 'already-sober' && styles.selectedCard
            ]}
            onPress={() => handleOptionSelect('already-sober')}
          >
            <Text style={styles.optionText}>
              {t('sobriety.clock.option2')}
            </Text>
            <View style={[
              styles.radioButton,
              selectedOption === 'already-sober' && styles.selectedRadio
            ]}>
              {selectedOption === 'already-sober' && <View style={styles.radioInner} />}
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.card,
              selectedOption === 'starting-today' && styles.selectedCard
            ]}
            onPress={() => handleOptionSelect('starting-today')}
          >
            <Text style={styles.optionText}>
              {t('sobriety.clock.option3')}
            </Text>
            <View style={[
              styles.radioButton,
              selectedOption === 'starting-today' && styles.selectedRadio
            ]}>
              {selectedOption === 'starting-today' && <View style={styles.radioInner} />}
            </View>
          </TouchableOpacity>
        </View>
      </View>

      <View style={[styles.buttonGroup, { paddingBottom: insets.bottom + 24 }]}>
        <PrimaryButton
          title={t('sobriety.clock.continue')}
          onPress={handleContinue}
          disabled={!selectedOption}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  cancelText: {
    color: '#757C85',
    textAlign: 'right',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 14,
  },
  section: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 24,
    flexDirection: 'column',
    flex: 1,
    width: '100%',
  },
  questionText: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
  },
  optionsGroup: {
    alignItems: 'flex-start',
    gap: 12,
    flexDirection: 'column',
    width: '100%',
  },
  card: {
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    width: '100%',
    height: 64,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  selectedCard: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  optionText: {
    color: '#151515',
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
  },
  radioButton: {
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  selectedRadio: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#FFFFFF',
  },
  radioInner: {
    backgroundColor: '#015480',
    width: 12,
    height: 12,
    borderRadius: 6,
    flexShrink: 0,
  },
  buttonGroup: {
    paddingHorizontal: 24,
    paddingTop: 24,
    alignItems: 'flex-start',
    gap: 8,
    flexDirection: 'column',
    width: '100%',
  },
});
